import { type KeyboardEvent, type ReactNode, useId } from 'react'
import { cx } from '../cx'
import styles from './RadioGroup.module.css'

export type RadioOption = {
  value: string
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}

/**
 * A single choice from a short list, as a `fieldset` with a `legend`.
 *
 * Controlled: pass `value` and `onChange`. A disabled group uses
 * `aria-disabled` rather than `disabled`, so it stays in the tab order and the
 * arrow keys still move focus; `onChange` is not called and React restores the
 * checked option. `disabledReason` shows under the legend while disabled.
 * `error` is announced and wired to the group with `aria-describedby`.
 */
export function RadioGroup({
  label,
  value,
  onChange,
  options,
  name,
  disabled,
  disabledReason,
  error,
  className,
}: {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  options: readonly RadioOption[]
  name?: string
  disabled?: boolean
  disabledReason?: ReactNode
  error?: ReactNode
  className?: string
}) {
  const id = useId()
  const groupName = name ?? id
  const reasonId = `${id}-reason`
  const errorId = `${id}-error`
  const showReason = Boolean(disabled && disabledReason)
  const describedBy = [
    showReason ? reasonId : undefined,
    error ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  // Home/End jump to the first/last enabled option and select it (card
  // Accessibility); the arrows are the browser's own. A disabled group moves
  // focus without changing the value, as its arrows do.
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Home' && event.key !== 'End') return
    const enabled = options.filter((o) => !o.disabled)
    const target = event.key === 'Home' ? enabled[0] : enabled.at(-1)
    if (!target) return
    event.preventDefault()
    const inputs = event.currentTarget
      .closest('fieldset')
      ?.querySelectorAll<HTMLInputElement>('input[type="radio"]')
    Array.from(inputs ?? [])
      .find((input) => input.value === target.value)
      ?.focus()
    if (!disabled) onChange(target.value)
  }

  return (
    <fieldset
      // The explicit role keeps the group a `radiogroup` for assistive tech
      // while the `fieldset` and `legend` supply the name.
      // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: radiogroup on a fieldset is the specified pattern
      role="radiogroup"
      className={cx(styles.group, className)}
      aria-disabled={disabled || undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy || undefined}
    >
      <legend className={styles.legend}>{label}</legend>
      {showReason && (
        <p id={reasonId} className={styles.reason}>
          {disabledReason}
        </p>
      )}
      {options.map((o, i) => {
        const descId = `${id}-${i}-desc`
        return (
          <label
            key={o.value}
            className={styles.option}
            data-description={o.description ? '' : undefined}
          >
            <input
              type="radio"
              className={styles.dot}
              name={groupName}
              value={o.value}
              checked={value === o.value}
              // A locked option stays focusable and ignores activation
              // (component-states), like a locked group.
              aria-disabled={disabled || o.disabled || undefined}
              aria-describedby={o.description ? descId : undefined}
              onChange={() => {
                if (!disabled && !o.disabled) onChange(o.value)
              }}
              onKeyDown={onKeyDown}
            />
            <span className={styles.text}>
              {o.label}
              {o.description && (
                <span id={descId} className={styles.description}>
                  {o.description}
                </span>
              )}
            </span>
          </label>
        )
      })}
      {error && (
        <p id={errorId} role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </fieldset>
  )
}
